import { Copy, ExternalLink, KeyRound, RotateCcw } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "../../../components/Spinner";
import { apiRequest } from "../../../lib/api";
import { formatTimestamp, relativeTime } from "../../../lib/format";
import { useInvalidateAll, useLendingOverview } from "../../../lib/queries";
import type { ApiEnvelope, LenderAccount, LendingReviewer } from "../../../types";
import { ConfirmButton, Feedback, Field, Pill, useAction } from "../shared";
import { LendingFrame } from "./LendingShared";

interface Credentials { user: LenderAccount; temporaryPassword: string; reason: "created" | "reset" }

/**
 * Peso a Peso reviewer accounts. An administrator creates each one here; the temporary password is
 * shown exactly once and handed over out of band, and the reviewer changes it from the portal. A
 * forgotten password is replaced the same way, and an access is withdrawn by suspending the account.
 */
export function AdminLendingReviewersPage() {
  const overview = useLendingOverview();
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [credentials, setCredentials] = useState<Credentials | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setCredentials(null);
    await action.run("create", async () => {
      const result = await apiRequest<ApiEnvelope<{ user: LenderAccount; temporaryPassword: string }>>("/api/v1/admin/lenders", {
        method: "POST", body: JSON.stringify({ fullName: fullName.trim(), email: email.trim() })
      });
      setCredentials({ ...result.data, reason: "created" });
      setFullName(""); setEmail("");
      await invalidate();
      return "Acceso creado. Comparte la contraseña temporal por un canal seguro; no volverá a mostrarse.";
    });
  };

  const reviewers = overview.data?.reviewers ?? [];
  return (
    <LendingFrame title="Revisores de Peso a Peso" subtitle="Quién puede entrar al portal de aliados y decidir sobre las solicitudes.">
      <div className="admin-two-col">
        <div className="admin-stack">
          <form className="card admin-form" onSubmit={submit}>
            <div className="section-heading"><div><span className="page-eyebrow">Nuevo acceso</span><h2>Dar de alta a un revisor</h2></div></div>
            <p className="field-hint">El revisor entra en <strong>{window.location.origin}/aliados/acceso</strong> y solo ve las solicitudes de crédito: no tiene wallet ni acceso a la administración.</p>
            <Field label="Nombre completo" htmlFor="l-name"><input id="l-name" className="standalone-input" value={fullName} onChange={(event) => setFullName(event.target.value)} minLength={2} maxLength={160} required /></Field>
            <Field label="Correo electrónico" htmlFor="l-email"><input id="l-email" className="standalone-input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></Field>
            <Feedback error={action.error} notice={action.notice} />
            {credentials && <CredentialsBox credentials={credentials} />}
            <button className="primary-button solid" type="submit" disabled={action.busy !== null}>{action.busy === "create" ? <Spinner /> : null} Crear acceso</button>
          </form>
          <section className="card">
            <span className="rail-title">Portal de aliados</span>
            <p className="field-hint">Tú también puedes entrar al portal con tu cuenta de administrador para ver exactamente lo que ve Peso a Peso.</p>
            <div className="inline-actions"><Link className="secondary-button small" to="/aliados"><ExternalLink size={14} aria-hidden="true" /> Abrir portal de aliados</Link></div>
          </section>
        </div>
        <section className="card">
          <div className="section-heading small"><div><span className="page-eyebrow">Revisores</span><h2>Accesos</h2></div><span>{reviewers.length}</span></div>
          {overview.isLoading && <Spinner label="Cargando" />}
          {overview.data && reviewers.length === 0 && <p className="rail-empty">Todavía no hay revisores dados de alta.</p>}
          {reviewers.length > 0 && (
            <div className="reviewer-list">
              {reviewers.map((reviewer) => <ReviewerRow key={reviewer.id} reviewer={reviewer} onCredentials={setCredentials} />)}
            </div>
          )}
          <p className="field-hint">Suspender un acceso cierra sus sesiones al instante y bloquea el inicio de sesión; puede reactivarse después. El historial de sus decisiones se conserva.</p>
        </section>
      </div>
    </LendingFrame>
  );
}

function CredentialsBox({ credentials }: { credentials: Credentials }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Portal: ${window.location.origin}/aliados/acceso\nUsuario: ${credentials.user.email}\nContraseña temporal: ${credentials.temporaryPassword}`);
      setCopied(true);
    } catch { setCopied(false); }
  };
  return (
    <div className="lender-credentials">
      <span className="rail-title"><KeyRound size={13} aria-hidden="true" /> {credentials.reason === "created" ? "Contraseña temporal" : "Nueva contraseña temporal"} · se muestra una sola vez</span>
      <code translate="no">{credentials.temporaryPassword}</code>
      <div className="inline-actions">
        <button type="button" className="secondary-button small" onClick={() => void copy()}><Copy size={14} aria-hidden="true" /> {copied ? "Copiado" : "Copiar acceso completo"}</button>
        <span className="field-hint">Usuario: {credentials.user.email}</span>
      </div>
    </div>
  );
}

function ReviewerRow({ reviewer, onCredentials }: { reviewer: LendingReviewer; onCredentials(value: Credentials): void }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const suspended = reviewer.status === "suspended";
  const toggle = async () => {
    await apiRequest(`/api/v1/admin/users/${reviewer.id}`, { method: "PATCH", body: JSON.stringify({ status: suspended ? "active" : "suspended" }) });
    await invalidate();
  };
  const reset = async () => {
    const result = await apiRequest<ApiEnvelope<{ user: LenderAccount; temporaryPassword: string }>>(`/api/v1/admin/lenders/${reviewer.id}/reset-password`, { method: "POST" });
    onCredentials({ ...result.data, reason: "reset" });
    await invalidate();
  };
  return (
    <div className="reviewer-row">
      <div className="reviewer-main">
        <Link to={`/admin/usuarios/${reviewer.id}`} className="table-primary"><strong>{reviewer.fullName}</strong><span>{reviewer.email}</span></Link>
        <Pill status={reviewer.status} />
      </div>
      <div className="meta-row">
        <span>{reviewer.decisions} decisión(es)</span>
        <span>Último acceso {reviewer.lastLoginAt ? relativeTime(reviewer.lastLoginAt) : "nunca"}</span>
        <span>Alta {formatTimestamp(reviewer.createdAt)}</span>
      </div>
      <div className="row-actions">
        <ConfirmButton
          label="Nueva contraseña" icon={<RotateCcw size={14} aria-hidden="true" />}
          title="Restablecer la contraseña del revisor"
          message={<p>Se generará una contraseña temporal para <strong>{reviewer.email}</strong> y se cerrarán todas sus sesiones. Tendrás que compartírsela por un canal seguro.</p>}
          confirmLabel="Generar contraseña" onConfirm={reset}
        />
        <ConfirmButton
          danger={!suspended} label={suspended ? "Reactivar acceso" : "Suspender acceso"}
          title={suspended ? "Reactivar este acceso" : "Suspender este acceso"}
          message={suspended ? <p><strong>{reviewer.fullName}</strong> podrá volver a entrar al portal de aliados con su contraseña actual.</p> : <p><strong>{reviewer.fullName}</strong> ya no podrá entrar al portal y sus sesiones abiertas se cerrarán. Puedes reactivarlo cuando quieras.</p>}
          confirmLabel={suspended ? "Reactivar" : "Suspender"} onConfirm={toggle}
        />
      </div>
      <Feedback error={action.error} notice={action.notice} />
    </div>
  );
}
